import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    Divider,
    Drawer,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const ROLE_ADMIN = 1;
const ROLE_CREATOR = 2;
const ROLE_SUBACCOUNT = 9;
const HIERARCHY_ROLE_IDS = new Set([ROLE_ADMIN, ROLE_CREATOR, ROLE_SUBACCOUNT]);

/**
 * Convert role ids to short labels for scope selectors.
 */
const getRoleLabel = (roleId) => {
    if (roleId === ROLE_ADMIN) {
        return 'Admin';
    }
    if (roleId === ROLE_CREATOR) {
        return 'Owner';
    }
    if (roleId === ROLE_SUBACCOUNT) {
        return 'Subaccount';
    }
    return 'Account';
};

/**
 * Format account option labels with role context.
 */
const formatClientLabel = (client) => {
    const roleLabel = getRoleLabel(Number(client.role_id));
    return `${client.name} (${roleLabel})`;
};

/**
 * Resolve a human-readable scope label for context rows.
 */
const getScopeLabel = (context, clientNameById) => {
    if (context.applies_to_all_clients) {
        return 'All Clients';
    }

    const scopeId = context.scope_client_id || context.id_client;
    return context.scope_client_name || clientNameById.get(String(scopeId)) || `#${scopeId}`;
};

/**
 * Parse the extra JSON textarea into an object for enrichment payloads.
 */
const parseJson = (value) => {
    if (!value) {
        return {};
    }

    try {
        return JSON.parse(value);
    } catch (error) {
        return null;
    }
};

/**
 * Normalize form values into the payload shape expected by enrichment contexts.
 */
const normalizeContextPayload = (extraJson) => ({
    extra: extraJson || {},
});

const createOption = (id) => ({
    id,
    value: '',
    label: '',
});

const createSelectField = (id, optionId) => ({
    id,
    type: 'select',
    key: '',
    label: '',
    options: [createOption(optionId)],
});

const createToggleField = (id) => ({
    id,
    type: 'toggle',
    key: '',
    label: '',
    true_label: '',
    false_label: '',
});

const buildUiSchema = (fields) => ({
    fields: fields.map((field) => {
        const base = {
            type: field.type,
            key: field.key?.trim() || '',
            label: field.label?.trim() || '',
        };

        if (field.type === 'select') {
            return {
                ...base,
                options: (field.options || []).map((option) => ({
                    value: option.value?.trim() || '',
                    label: option.label?.trim() || '',
                })),
            };
        }

        return {
            ...base,
            true_label: field.true_label?.trim() || '',
            false_label: field.false_label?.trim() || '',
        };
    }),
});

const normalizeFieldsFromSchema = (schema, nextId) => {
    const rawFields = Array.isArray(schema?.fields) ? schema.fields : [];
    return rawFields.map((field) => {
        const id = nextId();
        if (field?.type === 'toggle') {
            return {
                id,
                type: 'toggle',
                key: field.key || '',
                label: field.label || '',
                true_label: field.true_label || '',
                false_label: field.false_label || '',
            };
        }

        return {
            id,
            type: 'select',
            key: field?.key || '',
            label: field?.label || '',
            options: Array.isArray(field?.options)
                ? field.options.map((option) => ({
                    id: nextId(),
                    value: option?.value || '',
                    label: option?.label || '',
                }))
                : [createOption(nextId())],
        };
    });
};

const normalizeFieldValues = (fields, currentValues = {}) => {
    const nextValues = {};
    fields.forEach((field) => {
        const key = field.key?.trim();
        if (!key) {
            return;
        }
        if (Object.prototype.hasOwnProperty.call(currentValues, key)) {
            nextValues[key] = currentValues[key];
            return;
        }
        nextValues[key] = field.type === 'toggle' ? false : '';
    });
    return nextValues;
};

/**
 * Render the analyzer enrichment context management surface.
 */
const AnalyzerEnrichmentPage = ({
    clients,
    models,
    groups,
    listUrl,
    groupsUrl,
    canSelectAllClients,
    upsertUrl,
    deleteUrlTemplate,
}) => {
    const [clientId, setClientId] = useState('');
    const [contexts, setContexts] = useState([]);
    const [groupOptions, setGroupOptions] = useState(groups || []);
    const [groupName, setGroupName] = useState('');
    const [modelId, setModelId] = useState('');
    const [apiEndpoint, setApiEndpoint] = useState('');
    const [authEndpoint, setAuthEndpoint] = useState('');
    const [authPayloadText, setAuthPayloadText] = useState('{\n  "email": "",\n  "password": ""\n}');
    const [tokenPath, setTokenPath] = useState('data.token');
    const [tokenPrefix, setTokenPrefix] = useState('Bearer');
    const [adapterKey, setAdapterKey] = useState('default');
    const [extraJsonText, setExtraJsonText] = useState('{}');
    const [active, setActive] = useState(true);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [fields, setFields] = useState([]);
    const [fieldValues, setFieldValues] = useState({});
    const [editingId, setEditingId] = useState(null);
    const [editingReadOnly, setEditingReadOnly] = useState(false);
    const [cascadeToDescendants, setCascadeToDescendants] = useState(false);
    const [detailsContext, setDetailsContext] = useState(null);
    const idCounter = useRef(1);

    const nextId = () => {
        const current = idCounter.current;
        idCounter.current += 1;
        return current;
    };

    const parsedExtraJson = useMemo(() => parseJson(extraJsonText), [extraJsonText]);
    const parsedAuthPayload = useMemo(() => parseJson(authPayloadText), [authPayloadText]);
    const modelNameById = useMemo(() => {
        const map = new Map();
        models.forEach((model) => {
            map.set(String(model.id), model.name);
        });
        return map;
    }, [models]);

    const clientNameById = useMemo(() => {
        const map = new Map();
        clients.forEach((client) => {
            map.set(String(client.id), client.name);
        });
        return map;
    }, [clients]);

    const clientOptions = useMemo(
        () => clients.filter((client) => HIERARCHY_ROLE_IDS.has(Number(client.role_id))),
        [clients]
    );
    const isAllClientsSelected = canSelectAllClients && clientId === 'all';

    const resetForm = () => {
        setClientId('');
        setGroupName('');
        setModelId('');
        setApiEndpoint('');
        setAuthEndpoint('');
        setAuthPayloadText('{\n  "email": "",\n  "password": ""\n}');
        setTokenPath('data.token');
        setTokenPrefix('Bearer');
        setAdapterKey('default');
        setExtraJsonText('{}');
        setActive(true);
        setFields([]);
        setFieldValues({});
        setEditingId(null);
        setEditingReadOnly(false);
        setCascadeToDescendants(false);
        setError('');
    };

    const loadContexts = async () => {
        if (!listUrl) {
            return;
        }

        try {
            const response = await fetch(listUrl, { headers: { Accept: 'application/json' } });
            const payload = await response.json();
            setContexts(payload.data || []);
        } catch (fetchError) {
            console.error(fetchError);
        }
    };

    const loadGroups = async (nextClientId = clientId) => {
        if (!groupsUrl) {
            setGroupOptions(groups || []);
            return;
        }

        try {
            const params = new URLSearchParams();
            if (nextClientId) {
                params.set('client_id', nextClientId);
            }
            const url = params.toString() ? `${groupsUrl}?${params.toString()}` : groupsUrl;
            const response = await fetch(url, { headers: { Accept: 'application/json' } });
            const payload = await response.json();
            setGroupName('');
            setGroupOptions(payload.data || []);
        } catch (fetchError) {
            console.error(fetchError);
        }
    };

    useEffect(() => {
        if (clientOptions.length > 0 && !clientId) {
            setClientId(String(clientOptions[0].id));
        }
    }, [clientOptions, clientId]);

    useEffect(() => {
        loadContexts();
    }, [listUrl]);

    useEffect(() => {
        loadGroups(clientId);
    }, [groupsUrl, clientId]);

    useEffect(() => {
        setFieldValues((prev) => normalizeFieldValues(fields, prev));
    }, [fields]);

    const validateFields = () => {
        if (fields.length === 0) {
            return 'Add at least one field.';
        }

        for (const field of fields) {
            if (!field.key?.trim() || !field.label?.trim()) {
                return 'Each field requires a name and label.';
            }
            if (field.type === 'select') {
                const options = field.options || [];
                const validOptions = options.filter((option) => option.value?.trim() && option.label?.trim());
                if (validOptions.length === 0) {
                    return `Dropdown "${field.key}" needs at least one option.`;
                }
                const fieldKey = field.key?.trim();
                if (fieldKey && (fieldValues[fieldKey] === undefined || fieldValues[fieldKey] === '')) {
                    return `Select a default value for "${fieldKey}".`;
                }
            }
        }

        return '';
    };

    const handleSave = async () => {
        setError('');
        if (!modelId) {
            setError('Please select a model.');
            return;
        }
        if (editingReadOnly) {
            setError('This context is locked by an admin.');
            return;
        }
        if (!apiEndpoint) {
            setError('Please enter the enrichment API endpoint.');
            return;
        }
        if (parsedExtraJson === null) {
            setError('Extra JSON must be valid.');
            return;
        }
        if (authEndpoint && parsedAuthPayload === null) {
            setError('Auth payload JSON must be valid.');
            return;
        }

        const fieldError = validateFields();
        if (fieldError) {
            setError(fieldError);
            return;
        }

        const payload = normalizeContextPayload(parsedExtraJson || {});
        const uiSchema = buildUiSchema(fields);
        const uiValues = normalizeFieldValues(fields, fieldValues);

        setLoading(true);
        try {
            const response = await fetch(upsertUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                    'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
                },
                body: JSON.stringify({
                    id: editingId,
                    client_id: clientId && clientId !== 'all' ? Number(clientId) : null,
                    applies_to_all_clients: clientId === 'all',
                    cascade_to_descendants: cascadeToDescendants,
                    group_name: groupName || null,
                    model_id: modelId || null,
                    api_endpoint: apiEndpoint || null,
                    auth_endpoint: authEndpoint || null,
                    auth_payload: authEndpoint ? (parsedAuthPayload || {}) : null,
                    token_path: tokenPath || null,
                    token_prefix: tokenPrefix || null,
                    adapter_key: adapterKey || 'default',
                    payload,
                    ui_schema: uiSchema,
                    ui_values: uiValues,
                    active,
                }),
            });

            if (!response.ok) {
                throw new Error('Failed to save context');
            }

            await loadContexts();
            resetForm();
        } catch (saveError) {
            console.error(saveError);
            setError('Failed to save context.');
        } finally {
            setLoading(false);
        }
    };

    const handleAddField = (type) => {
        const fieldId = nextId();
        const newField = type === 'toggle'
            ? createToggleField(fieldId)
            : createSelectField(fieldId, nextId());
        setFields((prev) => [...prev, newField]);
    };

    const handleFieldChange = (fieldId, key, value) => {
        setFields((prev) => prev.map((field) => (
            field.id === fieldId ? { ...field, [key]: value } : field
        )));
    };

    const handleRemoveField = (fieldId) => {
        setFields((prev) => prev.filter((field) => field.id !== fieldId));
    };

    const handleAddOption = (fieldId) => {
        setFields((prev) => prev.map((field) => {
            if (field.id !== fieldId) {
                return field;
            }
            return {
                ...field,
                options: [...(field.options || []), createOption(nextId())],
            };
        }));
    };

    const handleOptionChange = (fieldId, optionId, key, value) => {
        setFields((prev) => prev.map((field) => {
            if (field.id !== fieldId) {
                return field;
            }
            return {
                ...field,
                options: (field.options || []).map((option) => (
                    option.id === optionId ? { ...option, [key]: value } : option
                )),
            };
        }));
    };

    const handleRemoveOption = (fieldId, optionId) => {
        setFields((prev) => prev.map((field) => {
            if (field.id !== fieldId) {
                return field;
            }
            return {
                ...field,
                options: (field.options || []).filter((option) => option.id !== optionId),
            };
        }));
    };

    const handleFieldValueChange = (fieldKey, value) => {
        setFieldValues((prev) => ({
            ...prev,
            [fieldKey]: value,
        }));
    };

    const handleEdit = (context) => {
        if (context.read_only) {
            setError('This context is locked by an admin and cannot be edited.');
            return;
        }

        const contextClientId = context.applies_to_all_clients
            ? 'all'
            : String(context.scope_client_id || context.id_client || '');

        setClientId(contextClientId);
        setGroupName(context.group_name || '');
        setModelId(context.model_id ? String(context.model_id) : '');
        setApiEndpoint(context.api_endpoint || '');
        setAuthEndpoint(context.auth_endpoint || '');
        setAuthPayloadText(JSON.stringify(context.auth_payload || {}, null, 2));
        setTokenPath(context.token_path || 'data.token');
        setTokenPrefix(context.token_prefix || 'Bearer');
        setAdapterKey(context.adapter_key || 'default');
        setActive(Boolean(context.active));
        setExtraJsonText(JSON.stringify(context.payload?.extra || {}, null, 2));
        const nextFields = normalizeFieldsFromSchema(context.ui_schema, nextId);
        setFields(nextFields);
        setFieldValues(normalizeFieldValues(nextFields, context.ui_values || {}));
        setEditingId(context.id || null);
        setEditingReadOnly(Boolean(context.read_only));
        setCascadeToDescendants(false);
        setError('');
    };

    const handleDelete = async (context) => {
        if (context.read_only) {
            setError('This context is locked by an admin and cannot be deleted.');
            return;
        }
        if (!deleteUrlTemplate) {
            setError('Delete URL is not configured.');
            return;
        }

        if (!window.confirm('Delete this enrichment context?')) {
            return;
        }

        setLoading(true);
        try {
            const deleteUrl = deleteUrlTemplate.replace('__ID__', context.id);
            const response = await fetch(deleteUrl, {
                method: 'DELETE',
                headers: {
                    Accept: 'application/json',
                    'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
                },
            });

            if (!response.ok) {
                throw new Error('Failed to delete context');
            }

            await loadContexts();
            if (editingId === context.id) {
                resetForm();
            }
        } catch (deleteError) {
            console.error(deleteError);
            setError('Failed to delete context.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Paper elevation={2} sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                    New Enrichment Context
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Define enrichment metadata per analyzer model and optional group.
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                        <FormControl fullWidth size="small">
                            <InputLabel id="client-select-label">Client</InputLabel>
                            <Select
                                labelId="client-select-label"
                                value={clientId}
                                label="Client"
                                onChange={(event) => setClientId(event.target.value)}
                            >
                                {canSelectAllClients && (
                                    <MenuItem value="all">All Clients</MenuItem>
                                )}
                                {clientOptions.map((client) => (
                                    <MenuItem key={client.id} value={String(client.id)}>
                                        {formatClientLabel(client)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <FormControl fullWidth size="small">
                            <InputLabel id="model-select-label">Analyzer Model</InputLabel>
                            <Select
                                labelId="model-select-label"
                                value={modelId}
                                label="Analyzer Model"
                                onChange={(event) => setModelId(event.target.value)}
                            >
                                {models.map((model) => (
                                    <MenuItem key={model.id} value={model.id}>
                                        {model.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <FormControl fullWidth size="small">
                            <InputLabel id="group-select-label">Group</InputLabel>
                            <Select
                                labelId="group-select-label"
                                value={groupName}
                                label="Group"
                                onChange={(event) => setGroupName(event.target.value)}
                            >
                                <MenuItem value="">All (Client)</MenuItem>
                                {groupOptions.map((group) => (
                                    <MenuItem key={group} value={group}>
                                        {group}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            label="Enrichment API Endpoint"
                            value={apiEndpoint}
                            onChange={(event) => setApiEndpoint(event.target.value)}
                            fullWidth
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            label="Login Endpoint (JWT)"
                            value={authEndpoint}
                            onChange={(event) => setAuthEndpoint(event.target.value)}
                            fullWidth
                            size="small"
                            helperText="Optional. If provided, we will POST the auth payload and extract a token."
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            label="Token Path"
                            value={tokenPath}
                            onChange={(event) => setTokenPath(event.target.value)}
                            fullWidth
                            size="small"
                            helperText="Dot path in login response. Default: data.token"
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            label="Token Prefix"
                            value={tokenPrefix}
                            onChange={(event) => setTokenPrefix(event.target.value)}
                            fullWidth
                            size="small"
                            helperText="Authorization header prefix. Default: Bearer"
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <FormControl fullWidth size="small">
                            <InputLabel id="adapter-select-label">Adapter</InputLabel>
                            <Select
                                labelId="adapter-select-label"
                                value={adapterKey}
                                label="Adapter"
                                onChange={(event) => setAdapterKey(event.target.value)}
                            >
                                <MenuItem value="default">Default</MenuItem>
                                <MenuItem value="parking_fee_check">ParkingFeeCheck</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label="Auth Payload JSON"
                            value={authPayloadText}
                            onChange={(event) => setAuthPayloadText(event.target.value)}
                            multiline
                            minRows={4}
                            fullWidth
                            helperText="Used for login endpoint. Credentials will be encrypted at rest."
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                            Enrichment Fields
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Build the dropdowns and toggles that will appear in the analyzer enrichment UI.
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                            <Button variant="outlined" size="small" onClick={() => handleAddField('select')}>
                                Add Dropdown
                            </Button>
                            <Button variant="outlined" size="small" onClick={() => handleAddField('toggle')}>
                                Add Toggle
                            </Button>
                        </Box>
                        {fields.length === 0 ? (
                            <Typography color="text.secondary">No fields added yet.</Typography>
                        ) : (
                            <Box sx={{ display: 'grid', gap: 2 }}>
                                {fields.map((field) => (
                                    <Paper key={field.id} variant="outlined" sx={{ p: 2 }}>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} md={3}>
                                                <TextField
                                                    label="Name"
                                                    value={field.key}
                                                    onChange={(event) => handleFieldChange(field.id, 'key', event.target.value)}
                                                    fullWidth
                                                    size="small"
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={4}>
                                                <TextField
                                                    label="Label"
                                                    value={field.label}
                                                    onChange={(event) => handleFieldChange(field.id, 'label', event.target.value)}
                                                    fullWidth
                                                    size="small"
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={3}>
                                                <Typography variant="caption" color="text.secondary">Type</Typography>
                                                <Typography variant="body2">{field.type === 'toggle' ? 'Toggle' : 'Dropdown'}</Typography>
                                            </Grid>
                                            <Grid item xs={12} md={2}>
                                                <Button color="error" onClick={() => handleRemoveField(field.id)}>
                                                    Remove
                                                </Button>
                                            </Grid>
                                            {field.type === 'select' ? (
                                                <Grid item xs={12}>
                                                    <Box sx={{ display: 'grid', gap: 1 }}>
                                                        {(field.options || []).map((option) => (
                                                            <Grid container spacing={1} key={option.id} alignItems="center">
                                                                <Grid item xs={12} md={4}>
                                                                    <TextField
                                                                        label="Value"
                                                                        value={option.value}
                                                                        onChange={(event) => handleOptionChange(field.id, option.id, 'value', event.target.value)}
                                                                        fullWidth
                                                                        size="small"
                                                                    />
                                                                </Grid>
                                                                <Grid item xs={12} md={5}>
                                                                    <TextField
                                                                        label="Label"
                                                                        value={option.label}
                                                                        onChange={(event) => handleOptionChange(field.id, option.id, 'label', event.target.value)}
                                                                        fullWidth
                                                                        size="small"
                                                                    />
                                                                </Grid>
                                                                <Grid item xs={12} md={3}>
                                                                    <Button color="error" onClick={() => handleRemoveOption(field.id, option.id)}>
                                                                        Remove Option
                                                                    </Button>
                                                                </Grid>
                                                            </Grid>
                                                        ))}
                                                        <Button size="small" onClick={() => handleAddOption(field.id)}>
                                                            Add Option
                                                        </Button>
                                                    </Box>
                                                </Grid>
                                            ) : (
                                                <>
                                                    <Grid item xs={12} md={4}>
                                                        <TextField
                                                            label="True Label"
                                                            value={field.true_label}
                                                            onChange={(event) => handleFieldChange(field.id, 'true_label', event.target.value)}
                                                            fullWidth
                                                            size="small"
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} md={4}>
                                                        <TextField
                                                            label="False Label"
                                                            value={field.false_label}
                                                            onChange={(event) => handleFieldChange(field.id, 'false_label', event.target.value)}
                                                            fullWidth
                                                            size="small"
                                                        />
                                                    </Grid>
                                                </>
                                            )}
                                        </Grid>
                                    </Paper>
                                ))}
                            </Box>
                        )}
                    </Grid>
                    {fields.length > 0 && (
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                Default Values
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                These values are stored with the context and merged into future enrichment requests.
                            </Typography>
                            <Grid container spacing={2}>
                                {fields.map((field) => {
                                    const fieldKey = field.key?.trim() || '';
                                    if (!fieldKey) {
                                        return (
                                            <Grid item xs={12} md={6} key={`default-${field.id}`}>
                                                <Typography color="text.secondary">
                                                    Add a field name to configure defaults.
                                                </Typography>
                                            </Grid>
                                        );
                                    }

                                    if (field.type === 'toggle') {
                                        return (
                                            <Grid item xs={12} md={6} key={`default-${field.id}`}>
                                                <FormControlLabel
                                                    control={(
                                                        <Switch
                                                            checked={Boolean(fieldValues[fieldKey])}
                                                            onChange={(event) => handleFieldValueChange(fieldKey, event.target.checked)}
                                                        />
                                                    )}
                                                    label={field.label || fieldKey}
                                                />
                                                <Typography variant="caption" color="text.secondary">
                                                    True: {field.true_label || 'Yes'} / False: {field.false_label || 'No'}
                                                </Typography>
                                            </Grid>
                                        );
                                    }

                                    const selectOptions = (field.options || []).filter((option) => option.value?.trim());

                                    return (
                                        <Grid item xs={12} md={6} key={`default-${field.id}`}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel id={`default-${field.id}`}>{field.label || fieldKey}</InputLabel>
                                                <Select
                                                    labelId={`default-${field.id}`}
                                                    value={fieldValues[fieldKey] ?? ''}
                                                    label={field.label || fieldKey}
                                                    onChange={(event) => handleFieldValueChange(fieldKey, event.target.value)}
                                                >
                                                    {selectOptions.map((option) => (
                                                        <MenuItem key={option.value} value={option.value}>
                                                            {option.label || option.value}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        </Grid>
                    )}
                    <Grid item xs={12}>
                        <TextField
                            label="Extra JSON"
                            value={extraJsonText}
                            onChange={(event) => setExtraJsonText(event.target.value)}
                            multiline
                            minRows={4}
                            fullWidth
                            helperText='Merged into the enrichment request body under extra. Example: {"tip_scanare": "sector"}'
                        />
                    </Grid>
                    <Grid item xs={12}>
                        {!isAllClientsSelected && (
                            <>
                                <FormControlLabel
                                    control={(
                                        <Switch
                                            checked={cascadeToDescendants}
                                            onChange={(event) => setCascadeToDescendants(event.target.checked)}
                                        />
                                    )}
                                    label="Apply to all descendants of selected account"
                                />
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                    Owner cascades to subaccounts/dispatchers below it. Subaccount cascades to its child accounts.
                                </Typography>
                            </>
                        )}
                    </Grid>
                    <Grid item xs={12}>
                        <FormControlLabel
                            control={<Switch checked={active} onChange={(event) => setActive(event.target.checked)} />}
                            label="Active"
                        />
                    </Grid>
                    {error && (
                        <Grid item xs={12}>
                            <Typography color="error">{error}</Typography>
                        </Grid>
                    )}
                    <Grid item xs={12}>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Button variant="contained" color="primary" disabled={loading} onClick={handleSave}>
                                {loading ? 'Saving...' : editingId ? 'Update Context' : 'Save Context'}
                            </Button>
                            {editingId && (
                                <Button variant="outlined" onClick={resetForm}>
                                    Cancel Edit
                                </Button>
                            )}
                        </Box>
                    </Grid>
                </Grid>
            </Paper>

            <Paper elevation={2} sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                    Saved Contexts
                </Typography>
                <Divider sx={{ mb: 2 }} />
                {contexts.length === 0 ? (
                    <Typography color="text.secondary">No contexts configured yet.</Typography>
                ) : (
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Model</TableCell>
                                    <TableCell>Group</TableCell>
                                    <TableCell>Scope</TableCell>
                                    <TableCell>Adapter</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Updated</TableCell>
                                    <TableCell align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {contexts.map((context) => (
                                    <TableRow key={context.id} hover>
                                        <TableCell>
                                            {context.model_id
                                                ? modelNameById.get(String(context.model_id)) || `#${context.model_id}`
                                                : 'All'}
                                        </TableCell>
                                        <TableCell>{context.group_name || 'Client default'}</TableCell>
                                        <TableCell>
                                            {getScopeLabel(context, clientNameById)}
                                        </TableCell>
                                        <TableCell>{context.adapter_key || 'default'}</TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                color={context.active ? 'success' : 'default'}
                                                label={context.active ? 'Active' : 'Disabled'}
                                            />
                                        </TableCell>
                                        <TableCell>{context.updated_at}</TableCell>
                                        <TableCell align="right">
                                            <Tooltip title="Details">
                                                <IconButton size="small" onClick={() => setDetailsContext(context)}>
                                                    <InfoOutlinedIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            {!context.read_only ? (
                                                <>
                                                    <Button size="small" onClick={() => handleEdit(context)}>
                                                        Edit
                                                    </Button>
                                                    <Button size="small" color="error" onClick={() => handleDelete(context)}>
                                                        Delete
                                                    </Button>
                                                </>
                                            ) : (
                                                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                                    Locked
                                                </Typography>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>

            <Drawer
                anchor="right"
                open={Boolean(detailsContext)}
                onClose={() => setDetailsContext(null)}
                PaperProps={{ sx: { width: { xs: '100%', sm: 560 }, p: 2 } }}
            >
                {detailsContext && (
                    <Box sx={{ display: 'grid', gap: 2 }}>
                        <Typography variant="h6" fontWeight={600}>Context Details</Typography>
                        <Typography variant="body2">
                            {detailsContext.model_id
                                ? modelNameById.get(String(detailsContext.model_id)) || `#${detailsContext.model_id}`
                                : 'All Models'}
                            {' · '}
                            {detailsContext.group_name || 'Client default'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Scope: {getScopeLabel(detailsContext, clientNameById)}
                        </Typography>
                        <Box sx={{ p: 1.5, bgcolor: 'grey.100', borderRadius: 1, fontFamily: 'monospace', fontSize: '0.75rem', whiteSpace: 'pre-wrap' }}>
                            {JSON.stringify(detailsContext.payload, null, 2)}
                        </Box>
                        <Box sx={{ p: 1.5, bgcolor: 'grey.100', borderRadius: 1, fontFamily: 'monospace', fontSize: '0.75rem', whiteSpace: 'pre-wrap' }}>
                            {JSON.stringify(detailsContext.ui_schema, null, 2)}
                        </Box>
                        <Box sx={{ p: 1.5, bgcolor: 'grey.100', borderRadius: 1, fontFamily: 'monospace', fontSize: '0.75rem', whiteSpace: 'pre-wrap' }}>
                            {JSON.stringify(detailsContext.ui_values, null, 2)}
                        </Box>
                    </Box>
                )}
            </Drawer>
        </Box>
    );
};

export default AnalyzerEnrichmentPage;
